import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { uploadPostFile, uploadUrl } from '../utils/postImages'
import UserAvatar from './userAvatar'
import { hasStory, storyFile } from '../utils/story'
import * as authAction from '../store/authAction'
import '../styles/story.css'

export const StoryRing = ({ has, className, onClick, children, label }) => (
  <button
    type="button"
    className={`storyRing${has ? ' hasStory' : ''}${className ? ` ${className}` : ''}`}
    onClick={onClick}
    aria-label={label || (has ? 'View story' : 'Profile photo')}
  >
    {children}
  </button>
)

const StoryViewer = ({
  open,
  onClose,
  media,
  name,
  photo,
  canAdd,
  items,
  index = 0,
  onIndex
}) => {
  const dispatch = useDispatch()
  const myStory = useSelector(state => state.authReduse.story)
  const list = Array.isArray(items) && items.length
    ? items
    : (open ? [{ story: media, name, photo, canAdd }] : [])
  const i = Math.max(0, Math.min(Number(index) || 0, Math.max(0, list.length - 1)))
  const current = list[i] || {}
  const currentCanAdd = Boolean(current.canAdd)
  const packed = currentCanAdd ? (myStory || current.story) : current.story
  const fileRef = useRef(null)
  const touchX = useRef(null)
  const [composer, setComposer] = useState(false)
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const storySrc = composer ? preview : (hasStory(packed) ? uploadUrl(storyFile(packed)) : '')

  const goNext = () => {
    if (i < list.length - 1) onIndex?.(i + 1)
    else onClose?.()
  }

  const goPrev = () => {
    if (i > 0) onIndex?.(i - 1)
    else onClose?.()
  }

  useEffect(() => {
    if (!open) {
      setComposer(false)
      setFile(null)
      setError('')
      return
    }
    if (currentCanAdd && !hasStory(packed)) setComposer(true)
    else setComposer(false)
  }, [open, currentCanAdd, packed, i])

  useEffect(() => {
    if (!file) {
      setPreview('')
      return undefined
    }
    const url = URL.createObjectURL(file)
    setPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  useEffect(() => {
    if (!open || composer) return undefined
    const onKey = event => {
      if (event.key === 'Escape') onClose?.()
      if (event.key === 'ArrowRight') goNext()
      if (event.key === 'ArrowLeft') goPrev()
    }
    window.addEventListener('keydown', onKey)
    const timer = window.setTimeout(goNext, 7000)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.clearTimeout(timer)
    }
  }, [open, composer, i, packed])

  if (!open) return null

  const pickFile = event => {
    const next = event.target.files && event.target.files[0]
    if (next && String(next.type || '').startsWith('image/')) {
      setFile(next)
      setComposer(true)
      setError('')
    }
    event.target.value = ''
  }

  const share = async () => {
    if (!file) {
      fileRef.current?.click()
      return
    }
    setSaving(true)
    setError('')
    try {
      const savedName = await uploadPostFile(file, 0)
      await dispatch(authAction.update_story(savedName))
      setComposer(false)
      setFile(null)
    } catch (err) {
      setError(err.message || 'Could not share story')
    } finally {
      setSaving(false)
    }
  }

  const onMediaClick = event => {
    if (composer) return
    const x = event.clientX
    const width = event.currentTarget.clientWidth || window.innerWidth
    if (x < width * 0.28) goPrev()
    else goNext()
  }

  return createPortal(
    <div
      className="storyOverlay"
      role="dialog"
      aria-modal="true"
      onTouchStart={event => { touchX.current = event.changedTouches[0].clientX }}
      onTouchEnd={event => {
        if (composer || touchX.current == null) return
        const dx = event.changedTouches[0].clientX - touchX.current
        if (dx > 56) goPrev()
        else if (dx < -56) goNext()
      }}
    >
      <div className="storyTop">
        <div className="storyProgressRow">
          {list.map((item, n) => (
            <span key={item.id || n} className={n < i ? 'isDone' : n === i ? 'isLive' : ''}>
              {n === i && !composer ? <i key={`${i}-${packed}`} /> : null}
            </span>
          ))}
        </div>
        <div className="storyUser">
          <UserAvatar src={current.photo || photo} name={current.name || name} alt="" />
          <strong>{current.name || name || 'Story'}</strong>
        </div>
        <button type="button" className="storyClose" onClick={(event) => { event.stopPropagation(); onClose() }} aria-label="Close">×</button>
      </div>

      {storySrc ? (
        <img className="storyMedia" src={storySrc} alt="" onClick={onMediaClick} />
      ) : (
        <div className="storyEmpty" onClick={() => currentCanAdd && fileRef.current?.click()}>
          {currentCanAdd ? 'Tap to add a story' : 'No story yet'}
        </div>
      )}

      {currentCanAdd && (
        <div className="storyActions">
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={pickFile} />
          {composer && file ? (
            <>
              {error ? <p className="storyError">{error}</p> : null}
              <button type="button" className="storyShare" onClick={share} disabled={saving}>
                {saving ? 'Sharing…' : 'Share story'}
              </button>
              <button type="button" className="storyGhost" onClick={onClose} disabled={saving}>Cancel</button>
            </>
          ) : (
            <button type="button" className="storyShare" onClick={() => fileRef.current?.click()}>
              {hasStory(packed) ? 'New story' : 'Add story'}
            </button>
          )}
        </div>
      )}
    </div>,
    document.body
  )
}

export default StoryViewer
